import React, {useContext, useEffect, useState} from 'react';
import {StyleSheet, Text, View, Pressable} from 'react-native';
import {GlobalStateContext} from '../components/general/GlobalStateProvider';
import CampsListView from './explore_screen/components/views/CampsListView';
import DataOps from '../data/DataOps';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useNavigate } from 'react-router-dom';

const FavoritesScreen = () => {
    const [camps, setCamps] = useState([]);
    const [sessions, setSessions] = useState([]);
    const {padding: padding, likedCamps: likedCamps, likedSessions: likedSessions, user, isLoadingUser, setShowLoginModal} = useContext(GlobalStateContext);
    const navigate = useNavigate();
    
    useEffect(() => {
        // Only show login modal if loading is complete and user is not found
        if (!isLoadingUser && !user) {
            setShowLoginModal(true);
            return;
        }
    }, [user, isLoadingUser, setShowLoginModal]);

    useEffect(() => {
        const fetchFavoriteCamps = async () => {
            let favoriteCamps = [];
            for (const campId of likedCamps) {
                try {
                    if (typeof campId !== 'string') { // there could still be some testing IDs that are numbers.
                        continue;
                    }

                    const campData = await DataOps.fetchDataFromFirebase('camp', campId);
                    if (campData) {
                        favoriteCamps.push(campData);
                    }
                } catch (error) {
                    console.error('Error fetching camps:', error);
                }
            }

            // Compute activeSessionCount for favorites (like Explore page)
            const enriched = await Promise.all(
                favoriteCamps.map(async (camp) => {
                    try {
                        const activeCount = camp.activeSessionCount != null
                            ? camp.activeSessionCount
                            : await DataOps.getUpcomingSessionCount(camp.campId);
                        return { ...camp, activeSessionCount: activeCount };
                    } catch {
                        return { ...camp, activeSessionCount: 0 };
                    }
                })
            );

            const newCamps = enriched.sort(
                (sessionA, sessionB) => {
                    const lengthA = (sessionA.matchingSessionsCount ?? 0);
                    const lengthB = (sessionB.matchingSessionsCount ?? 0);
                    return lengthB - lengthA;
                }
            );

            setCamps(newCamps);
        };

        const unused_response = fetchFavoriteCamps();
    }, [likedCamps]);

    useEffect(() => {
        const fetchFavoriteSessions = async () => {
            let favoriteSessions = [];
            for (const sessionId of likedSessions) {
                try {
                    if (typeof sessionId !== 'string') {
                        continue;
                    }

                    const sessionData = await DataOps.fetchDataFromFirebase('session', sessionId);
                    if (sessionData) {
                        favoriteSessions.push(sessionData);
                    }
                } catch (error) {
                    console.error('Error fetching Sessions:', error);
                }
            }

            setSessions(favoriteSessions);
        };

        const unused_response = fetchFavoriteSessions();
    }, [likedSessions]);

    const hasFavoriteSessions = Array.isArray(sessions) && sessions.length > 0;
    const hasFavoriteCamps = Array.isArray(camps) && camps.length > 0;
    return (
        <View style={[styles.container, {paddingHorizontal: padding}]}>
            <View style={styles.tabsContainer}>
                <Text style={styles.titleText}>Favorite Sessions</Text>
                {hasFavoriteSessions ? (
                    <CampsListView camps={[]} sessions={sessions} isSessionView={true}/>
                ) : (
                    <View style={styles.emptyStateCard}>
                        <View style={styles.emptyIconWrap}>
                            <MaterialCommunityIcons name="calendar-heart" size={34} color="#DC2626" />
                        </View>
                        <Text style={styles.emptyTitle}>No favorite sessions yet</Text>
                        <Text style={styles.emptyDescription}>
                            Tap the heart on any session to save it here.
                        </Text>
                        <Pressable style={styles.emptyButton} onPress={() => navigate('/explore')}>
                            <MaterialCommunityIcons name="compass-outline" size={18} color="#FFFFFF" />
                            <Text style={styles.emptyButtonText}>Browse Sessions</Text>
                        </Pressable>
                    </View>
                )}

                <Text style={[styles.titleText, {marginTop: 30}]}>Favorite Camps</Text>
                {hasFavoriteCamps ? (
                    <CampsListView camps={camps} sessions={{}} isSessionView={false}/>
                ) : (
                    <View style={styles.emptyStateCard}>
                        <View style={styles.emptyIconWrap}>
                            <MaterialCommunityIcons name="home-heart" size={34} color="#DC2626" />
                        </View>
                        <Text style={styles.emptyTitle}>No favorite camps yet</Text>
                        <Text style={styles.emptyDescription}>
                            Save camps you like to quickly compare and revisit them.
                        </Text>
                        <Pressable style={styles.emptyButton} onPress={() => navigate('/explore')}>
                            <MaterialCommunityIcons name="compass-outline" size={18} color="#FFFFFF" />
                            <Text style={styles.emptyButtonText}>Explore Camps</Text>
                        </Pressable>
                    </View>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    description: {
        alignItems: 'center'
    },
    descriptionText: {
        fontSize: 18,
        fontWeight: '400',
    },
    titleText: {
        fontSize: 22,
        fontWeight: 'bold',
        marginBottom: 40,
    },
    container: {
        flex: 1,
        paddingHorizontal: 20,
        paddingTop: 20,
    },
    tabsContainer: {
        flexDirection: 'column',
        paddingHorizontal: 50,
        paddingTop: 20,
        marginBottom: 10,
    },
    emptyStateCard: {
        marginTop: 24,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 20,
        paddingVertical: 34,
        paddingHorizontal: 24,
        alignItems: 'center',
    },
    emptyIconWrap: {
        width: 68,
        height: 68,
        borderRadius: 34,
        backgroundColor: '#FEF2F2',
        borderWidth: 1,
        borderColor: '#FECACA',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 14,
    },
    emptyTitle: {
        fontSize: 24,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 8,
    },
    emptyDescription: {
        fontSize: 15,
        color: '#6B7280',
        textAlign: 'center',
        marginBottom: 18,
        maxWidth: 420,
        lineHeight: 22,
    },
    emptyButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#3C8248',
        borderRadius: 999,
        paddingHorizontal: 16,
        paddingVertical: 10,
    },
    emptyButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },
});

export default FavoritesScreen;